import { Home, Users, FileText, Menu, PanelLeft, ChevronLeft, DollarSign } from "lucide-react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button, Logo } from "@pix2bit/ui/components";
import { SettingsDropdown } from "./Logout";
import { getRouteConfig } from "../../config/routes";


export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();

  const { icon: PageIcon, title, subtitle } = getRouteConfig(location.pathname);

  return (
    <div className="flex h-screen">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />

      <div className="flex flex-col flex-1 min-w-0 relative z-10">
        <header className="border-b bg-white">
          <div className="flex items-center justify-between p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden flex-shrink-0"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>

              {/* Page Info */}
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <div className="p-1.5 sm:p-2 bg-[#F7931A]/10 rounded-lg flex-shrink-0">
                  <PageIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#F7931A]" />
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-base sm:text-lg font-bold truncate">{title}</h1>
                  <p className="text-xs text-gray-600 truncate">{subtitle}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <SettingsDropdown />
              <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-primary flex-shrink-0" />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-white">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  sidebarCollapsed,
  setSidebarCollapsed
}: {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [clickedPath, setClickedPath] = useState(null);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleClick = (path: any) => {
    setClickedPath(path);
    setSidebarOpen(false);
    setTimeout(() => {
      navigate(path);
    }, 0);
  };

  const toggleCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  useEffect(() => {
    if (clickedPath) {
      const timer = setTimeout(() => {
        setClickedPath(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [clickedPath]);

  const getLinkClasses = (path: string) => {
    return `flex items-center ${sidebarCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'}
      text-sm rounded-md transition-colors duration-200 ease-linear hover:bg-[#F7931A]/10 relative
      ${isActive(path) ? 'bg-[#F7931A]/20 text-[#F7931A]' : ''}
      ${clickedPath === path ? 'animate-pulse' : ''}`;
  };

  return (
    <div className={`
      fixed inset-y-0 left-0 bg-gray-50 border-r border-gray-200 lg:translate-x-0 lg:static lg:inset-0 z-50 lg:z-auto
      ${sidebarOpen ? 'translate-x-0 transition-transform duration-200 ease-linear' : '-translate-x-full transition-transform duration-200 ease-linear'}
      ${sidebarCollapsed ? 'w-20 transition-all duration-200 ease-linear' : 'w-64 transition-all duration-200 ease-linear'}
    `}>
      <div className="flex flex-col h-full p-4">
        <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} mb-6`}>
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-2'}`}>
            <Logo width={sidebarCollapsed ? "32" : "16"} height={sidebarCollapsed ? "20" : "16"} />
          </div>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleCollapse}
                className="size-7 hidden lg:flex"
              >
                <PanelLeft className="h-4 w-4" />
                <span className="sr-only">Toggle Sidebar</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden flex items-center gap-1"
                onClick={() => setSidebarOpen(false)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <nav className="flex-1 space-y-1">
          <a
            className={getLinkClasses("/dashboard/home")}
            href="#"
            onClick={() => handleClick("/dashboard/home")}
            title={sidebarCollapsed ? "Home" : ""}
          >
            <Home className={`${sidebarCollapsed ? 'h-6 w-6' : 'h-4 w-4'} ${isActive("/dashboard/home") ? "text-[#F7931A]" : ""}`} />
            {!sidebarCollapsed && <span>Home</span>}
          </a>
          <a
            className={getLinkClasses("/dashboard/users")}
            href="#"
            onClick={() => handleClick("/dashboard/users")}
            title={sidebarCollapsed ? "Users" : ""}
          >
            <Users className={`${sidebarCollapsed ? 'h-6 w-6' : 'h-4 w-4'} ${isActive("/dashboard/users") ? "text-[#F7931A]" : ""}`} />
            {!sidebarCollapsed && <span>Users</span>}
          </a>
          <a
            className={getLinkClasses("/dashboard/finance")}
            href="#"
            onClick={() => handleClick("/dashboard/finance")}
            title={sidebarCollapsed ? "Finance" : ""}
          >
            <DollarSign className={`${sidebarCollapsed ? 'h-6 w-6' : 'h-4 w-4'} ${isActive("/dashboard/finance") ? "text-[#F7931A]" : ""}`} />
            {!sidebarCollapsed && <span>Finance</span>}
          </a>
        </nav>
      </div>
    </div>
  );
}
